import { forwardRef, useRef, useEffect } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { typography } from '@/styles/typography';
import { Button } from '@/components/base/Button';
import CalendarIcon from '@/icons/Calendar.svg?react';
import ClockIcon from '@/icons/Clock.svg?react';
import GlobeIcon from '@/icons/Globe.svg?react';
import ShareIcon from '@/icons/Share.svg?react';
import GoToExternalSiteIcon from '@/icons/GoToExternalSite.svg?react';
import ResourceCatalogBlueIcon from '@/icons/ResourceCatalogBlue.svg?react';

/**
 * Theme/category color mapping for the pill badge
 */
export interface ThemeColors {
  /** Background color for the pill (hex with alpha, e.g., "#0098D366") */
  background: string;
  /** Border color for the pill (hex, e.g., "#0098D3") */
  border: string;
}

/**
 * Content interface for EventCard component.
 * All display text is passed via props for CMS integration.
 */
export interface EventCardContent {
  /** Unique event identifier */
  id: string;
  /** Event name/title */
  title: string;
  /** Organization/sponsor name */
  organization: string;
  /** Resource theme/category name */
  category: string;
  /** Event description */
  description: string;
  /** Day and date string (e.g., "Saturday, Feb 15") */
  date: string;
  /** Time range string (e.g., "10:00 AM - 11:00 AM") */
  time: string;
  /** Event website URL and display text (optional — omitted when URL is unavailable) */
  website?: {
    href: string;
    displayText: string;
  };
}

/**
 * Props for the EventCard component.
 */
export interface EventCardProps {
  /** Event data to display */
  content: EventCardContent;
  /** Theme color mapping for the category pill */
  themeColors: ThemeColors;
  /** Whether this event is currently saved in the catalog */
  isSaved?: boolean;
  /** Callback when share button is clicked */
  onShare?: (event: EventCardContent) => void;
  /** Callback when save resource is toggled */
  onToggleSave?: (eventId: string) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * EventCard - Individual event card for the Events page grid
 *
 * Displays event details including name, organization, category pill badge,
 * description, date, time, website link, and action buttons (share, external link,
 * save resource).
 *
 * @example
 * ```tsx
 * <EventCard
 *   content={{
 *     id: 'evt-001',
 *     title: 'Senior Fitness Workshop',
 *     organization: 'Columbus Senior Center',
 *     category: 'Ways to stay engaged',
 *     description: 'A fun fitness workshop for older adults.',
 *     date: 'Saturday, Feb 15',
 *     time: '10:00 AM - 11:00 AM',
 *     website: { href: 'https://example.org', displayText: 'example.org' },
 *   }}
 *   themeColors={{ background: '#FFD26B66', border: '#EBA70E' }}
 *   onShare={(event) => console.log('Share', event)}
 *   onToggleSave={(id) => console.log('Save', id)}
 * />
 * ```
 */
export const EventCard = forwardRef<HTMLElement, EventCardProps>(
  (
    {
      content,
      themeColors,
      isSaved = false,
      onShare,
      onToggleSave,
      className,
    },
    ref
  ) => {
    if (!content) return null;

    // Ref for save button focus restoration after state change (SHARED-DEFECT-005)
    const saveButtonRef = useRef<HTMLButtonElement>(null);
    const wasClickedRef = useRef(false);

    // Restore focus to save button after saved state changes when user clicked the button.
    // Uses requestAnimationFrame to run after all DOM updates and layout shifts
    // (e.g. catalog drawer opening) so focus isn't stolen by subsequent renders.
    useEffect(() => {
      if (wasClickedRef.current) {
        wasClickedRef.current = false;
        requestAnimationFrame(() => {
          saveButtonRef.current?.focus();
        });
      }
    }, [isSaved]);

    const handleShare = () => {
      onShare?.(content);
    };

    const handleToggleSave = () => {
      wasClickedRef.current = true;
      onToggleSave?.(content.id);
    };

    return (
      <article
        ref={ref}
        className={cn(
          'flex flex-col gap-1 rounded-[4px] border border-[#EBEBEB] bg-white p-4',
          className
        )}
        style={{
          boxShadow: '0px 4px 4px 0px rgba(187, 187, 187, 0.25)',
        }}
      >
        {/* Content area - grows to push action buttons to bottom */}
        <div className="flex flex-1 flex-col gap-1">
          {/* Event Name */}
          <h3 className={cn(typography.subtitle, 'text-foreground')}>
            {content.title}
          </h3>

          {/* Organization */}
          <p className={cn(typography.body, 'text-[#333333]')}>
            {content.organization}
          </p>

          {/* Category Pill Badge */}
          <div className="mt-1">
            <span
              className={cn(
                typography.buttonDefault,
                'inline-block rounded-2xl px-3 py-0.5 text-foreground'
              )}
              style={{
                backgroundColor: themeColors.background,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: themeColors.border,
              }}
            >
              {content.category}
            </span>
          </div>

          {/* Description */}
          <p className={cn(typography.body, 'text-foreground mt-1')}>
            {content.description}
          </p>

          {/* Date */}
          <div className="mt-2 flex items-center gap-2">
            <CalendarIcon
              className="h-5 w-5 flex-shrink-0 text-[#333333]"
              aria-hidden="true"
            />
            <span className={cn(typography.body, 'text-[#333333]')}>
              {content.date}
            </span>
          </div>

          {/* Time */}
          <div className="flex items-center gap-2">
            <ClockIcon
              className="h-5 w-5 flex-shrink-0 text-[#333333]"
              aria-hidden="true"
            />
            <span className={cn(typography.body, 'text-[#333333]')}>
              {content.time}
            </span>
          </div>

          {/* Website Link */}
          {content.website && (
            <div className="flex items-center gap-2">
              <GlobeIcon
                className="h-5 w-5 flex-shrink-0 text-[#0A4D8F]"
                aria-hidden="true"
              />
              <a
                href={content.website.href}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  typography.buttonDefault,
                  'text-[#0A4D8F] hover:underline',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm'
                )}
              >
                {content.website.displayText}
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            </div>
          )}
        </div>

        {/* Action Buttons - pinned to bottom via mt-auto */}
        <div className="mt-auto flex items-center justify-end gap-4 pt-3">
          {/* Share Button - standalone icon, no border/outline */}
          <button
            type="button"
            onClick={handleShare}
            className={cn(
              'inline-flex items-center justify-center',
              'appearance-none border-none bg-transparent text-button-primary',
              'hover:opacity-70',
              'cursor-pointer',
              'transition-opacity duration-150 motion-reduce:transition-none',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm'
            )}
            aria-label="Share"
          >
            <ShareIcon className="h-5 w-5" aria-hidden="true" />
          </button>

          {/* Go to External Site Button - standalone icon, no border/outline */}
          {content.website && (
            <a
              href={content.website.href}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'inline-flex items-center justify-center',
                'appearance-none border-none bg-transparent text-button-primary',
                'hover:opacity-70',
                'cursor-pointer',
                'transition-opacity duration-150 motion-reduce:transition-none',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm'
              )}
              aria-label="Go to website"
            >
              <GoToExternalSiteIcon className="h-5 w-5" aria-hidden="true" />
            </a>
          )}

          {/* Save resource Button - toggles between outline and primary variants */}
          <Button
            ref={saveButtonRef}
            variant={isSaved ? 'primary' : 'secondary'}
            size="medium"
            content={{
              text: isSaved ? 'Resource saved' : 'Save resource',
              icon: ResourceCatalogBlueIcon as unknown as LucideIcon,
            }}
            onClick={handleToggleSave}
            aria-pressed={isSaved}
            aria-label={
              isSaved
                ? 'Resource saved'
                : 'Save resource'
            }
          />
        </div>
      </article>
    );
  }
);

EventCard.displayName = 'EventCard';
